import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import Button from 'components/shared/Button';
import ButtonIcon from 'components/shared/Button/Icon';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Close from 'components/shared/Icon/icons/Close';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { AGENT_PERMISSION_CUSTOM_DOMAIN, useIsSubscriptionAllowed } from 'hooks/useIsSubscriptionAllowed';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { showSnackbar } from 'hooks/useSnackbar';
import { isAgentSelector } from 'store/user/selectors';

import { ACCOUNT_TYPE_AGENT } from '../../../../../../../../constants';
import { TailInfoResponse } from '../useProposalLink';
import s from './index.module.css';
import MobileModalWrapper from './MobileModalWrapper';

type ProposalLinkProps = {
  id: string;
  link: string;
  onCopyLink: (value: string) => void;
  onSaveLink: (tail: string) => Promise<void>;
  loadTailInfo: () => Promise<TailInfoResponse>;
};

function ProposalLink(props: ProposalLinkProps) {
  const { id, link, onCopyLink, onSaveLink, loadTailInfo } = props;

  const queryClient = useQueryClient();

  const [input, setInput] = useState<string | undefined>(undefined);
  const inputEmpty = input === undefined || input === '';

  const isAgent = useSelector(isAgentSelector);
  const isCustomDomainPermission = useIsSubscriptionAllowed(ACCOUNT_TYPE_AGENT, AGENT_PERMISSION_CUSTOM_DOMAIN);

  const { data, isFetching } = useQuery(['tail', id], loadTailInfo, {
    enabled: isCustomDomainPermission,
    staleTime: 300000,
  });

  const { isMobile } = useMediaBreakpoints();

  const mutation = useMutation<unknown, { errors?: { status: number } }, string>(onSaveLink, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['tail']);
      setInput(undefined);
      showSnackbar({
        text: 'Custom Link was successfully saved',
        uniqueId: 'custom-link-saved',
      });
    },
    onError: (err) => {
      let text;

      switch (err?.errors?.status) {
        case 409:
          text = 'This link is already in use. Please create a new one';
          break;
        default:
          if (typeof err === 'string') {
            text = err;
          } else {
            text = 'Unknown error. Please try again later';
          }
      }

      showSnackbar({
        text: text,
        type: SNACKBAR_SKIN.ERROR,
        uniqueId: 'custom-link-error',
      });
    },
  });
  const { domain, tail, secure } = (!isFetching && isCustomDomainPermission && data) || {};
  const splattedLink = link.split('/').slice(2);
  const fixedDomain = (domain || splattedLink[0]) + '/';
  const placeholder = tail || splattedLink.slice(1).join('/');
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const protocol = isFetching ? '' : `http${secure || (!domain && !isLocalhost) ? 's' : ''}://`;

  const isEditable = isAgent && isCustomDomainPermission && !!data?.domain;

  const renderLinkInput = () => {
    if (isMobile) {
      const Wrapper = isEditable ? MobileModalWrapper : 'div';
      return (
        <Wrapper className={s.slugInputWrapper}>
          <div className={s.slugInput}>
            <div className={s.slugDomain}>{fixedDomain + placeholder}</div>
          </div>
        </Wrapper>
      );
    }

    if (isEditable) {
      return (
        <div className={s.slugInput}>
          <div className={s.slugDomain}>{fixedDomain}</div>
          <input
            placeholder={placeholder}
            className={s.slugRealInput}
            value={input === undefined ? tail || '' : input}
            onChange={(e) => {
              setInput(e.target.value.replace(/\s+/g, '-'));
            }}
          />
        </div>
      );
    }

    return (
      <Field
        component={FormGroupAdapter}
        name="link"
        placeholder={isFetching ? '' : fixedDomain + placeholder}
        type="text"
        readOnly
        className={s.slugField}
      />
    );
  };

  return (
    <>
      <div className={s.slugWrapper}>
        <span className={s.https}>{protocol}</span>

        {renderLinkInput()}

        {inputEmpty && (
          <Button
            htmlType="button"
            className={s.slugCopy}
            onClick={() => onCopyLink(protocol + fixedDomain + (tail || placeholder || ''))}
          >
            Copy
          </Button>
        )}

        {!inputEmpty && (
          <>
            <ButtonIcon
              className={s.slugClear}
              component={Close}
              loading={false}
              width="2.3rem"
              height="2.3rem"
              onClick={() => setInput(undefined)}
            />
            <Button
              disabled={mutation.isLoading}
              htmlType="button"
              className={s.slugCopy}
              onClick={() => mutation.mutate(input)}
              children="Save"
            />
          </>
        )}
      </div>
    </>
  );
}

export default ProposalLink;
